import { Icon } from '@dayon/design-system';
import { formatCount } from '@dayon/money';

/**
 * How many people an announcement is for, and who is NOT in that number.
 *
 * One component rather than two blocks of JSX, because the composer and the
 * send confirmation must show the SAME figure with the same caveats. Two copies
 * would drift, and the drift would show up as a form promising 400 and a
 * confirmation promising 388 — at which point the operator has no idea which
 * one the platform believes.
 *
 * ── THREE STATES, AND THE THIRD IS NOT THE SECOND ─────────────────────────
 *
 * "not counted yet", "counted: N" and "the count did not come back" are three
 * different things, and the last must never render as a zero. A confident zero
 * produced by a request that failed is the exact figure somebody would send
 * against — and unlike a wrong number, it looks like good news.
 *
 * ── WHY THE SUBTRACTIONS ARE SHOWN RATHER THAN FOLDED IN ──────────────────
 *
 * `recipients` is smaller than `matched`, and an operator who cannot see why
 * will assume the rule is broken. The two reasons are different in kind and
 * both are worth reading:
 *
 *   · NO EMAIL ON FILE — the account cannot be reached at all. Email is the
 *     only channel with a transport, so these are unreachable rather than
 *     excluded, and the gap is a data gap somebody could close.
 *   · SELF-EXCLUDED, WITHHELD — a compliance subtraction, and it appears only
 *     on a promotion. Seeing it is how an operator learns the rule is enforced
 *     rather than merely documented.
 *   · NO CONSENT, WITHHELD — the other compliance subtraction, kept separate
 *     from the one above because "asked us to stop" and "was never asked" are
 *     different facts about a person. Folding them together would report a
 *     player who has never heard from this platform as self-excluded, which is
 *     a claim about them that is not true.
 */

export interface AudienceCount {
  rule: string;
  label: string;
  description: string;
  matched: number;
  withoutAddress: number;
  unconfirmedRecipients: number;
  selfExcludedWithheld: number;
  noConsentWithheld: number;
  recipients: number;
}

export function AudienceSummary({
  audience,
  counting,
  failed,
  kind,
}: {
  audience: AudienceCount | null;
  counting: boolean;
  failed: boolean;
  kind: string;
}) {
  if (failed) {
    return (
      <div className="notice" id="audience-count-failed">
        <Icon name="warning-circle" />
        <div>
          <strong>The audience could not be counted.</strong> The request did not come back, so
          there is no number here — not a number of zero. Nothing can be sent until it can be
          counted, because the count is what the send is confirmed against.
        </div>
      </div>
    );
  }

  if (counting && audience === null) {
    return (
      <div className="notice notice-quiet">
        <Icon name="hourglass" />
        <div>Counting the audience…</div>
      </div>
    );
  }

  if (audience === null) return null;

  const withheld = audience.selfExcludedWithheld;
  const unreachable = audience.withoutAddress;
  /* WHY THE NUMBER FELL, when it fell for this reason.
     `noConsentWithheld` arrived with the marketing-consent rule and was
     reported by the API but rendered nowhere, so the composer showed "2,017
     accounts match · 0 people receive this" and named no cause. An operator
     reading that concludes the resolver is broken — the one reading it as
     "nobody has agreed yet" would have to already know the rule exists. */
  const noConsent = audience.noConsentWithheld;
  /* HOW MUCH OF THE HEADLINE IS A GUESS.
     A promotion cannot have any of these — a confirmed address is part of
     being a recipient at all — so this only ever shows on an operational
     notice, which reaches an unconfirmed address on purpose. The number said
     "2,033 people receive this" while 2,021 of those addresses had been typed
     once and confirmed by nobody. Both facts are true; only one of them was on
     the screen. */
  const unconfirmed = audience.unconfirmedRecipients;

  return (
    <div className="notice notice-quiet" id="audience-count">
      <Icon name="users" />
      <div>
        <div>
          <strong className="tabular">{formatCount(audience.recipients)}</strong>{' '}
          {audience.recipients === 1 ? 'person receives this' : 'people receive this'}
          {counting ? ' · re-counting…' : ''}
        </div>
        <div className="text-subtle">
          {formatCount(audience.matched)}{' '}
          {audience.matched === 1 ? 'account matches' : 'accounts match'} {audience.description}
          {unreachable > 0 ? (
            <>
              {' · '}
              <span className="tabular">{formatCount(unreachable)}</span> unreachable, with no email
              address on file
            </>
          ) : null}
          {withheld > 0 ? (
            <>
              {' · '}
              <span className="tabular">{formatCount(withheld)}</span> withheld: they have
              self-excluded, and a promotion may not reach them
            </>
          ) : null}
          {noConsent > 0 ? (
            <>
              {' · '}
              <span className="tabular">{formatCount(noConsent)}</span> withheld: they have not
              agreed to marketing, or their address is unconfirmed
            </>
          ) : null}
        </div>
        {unconfirmed > 0 ? (
          <div className="text-subtle">
            <span className="tabular">{formatCount(unconfirmed)}</span> of those are at an address
            nobody has confirmed. An operational notice goes to them on purpose — a person has to
            be reachable about their own account before they get round to clicking a link — but an
            unconfirmed address may belong to nobody, or to somebody else, and these are the ones
            that bounce.
          </div>
        ) : null}
        {kind === 'MANDATORY' ? (
          <div className="text-subtle">
            A mandatory notice is operational, so a self-excluded player is <strong>included</strong>{' '}
            — it concerns an account they still hold and is not marketing they can switch off.
          </div>
        ) : null}
        {audience.recipients === 0 ? (
          <div>
            <strong>This reaches nobody.</strong>{' '}
            {noConsent > 0 ? (
              /* THE RULE MATCHED. Saying "matched no account" here would be
                 false — it matched every one of them and consent removed them
                 again, which is a different problem with a different fix. An
                 operator told the wrong one goes looking at the audience rule. */
              <>
                It is a real zero, and not an empty rule: the rule matched, and every account it
                matched is withheld for want of recorded marketing consent. A player grants it
                under <span className="tabular">Profile → Notifications</span> in the player app,
                and it is never assumed from having an account — so a promotional send has nobody
                to go to until players start agreeing. A <strong>mandatory</strong> notice is
                unaffected: it is operational, and does not rest on consent.
              </>
            ) : (
              <>
                It is a real zero — the rule was evaluated and matched no account in scope this
                platform can write to.
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
